import type {BaseQueryFn} from '@reduxjs/toolkit/query'

/** Client errors a retry can still resolve. */
const RETRYABLE_STATUSES = [408, 429]

const getStatus = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error
    ? (error as {status: unknown}).status
    : undefined

const isFinalClientError = (error: unknown) => {
  const status = getStatus(error)

  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    !RETRYABLE_STATUSES.includes(status)
  )
}

/**
 * Wraps a base query so errors a retry cannot fix resolve as an empty success,
 * which lets RTKQ cache them.
 *
 * RTKQ refetches every cache entry without a `fulfilledTimeStamp` as soon as a
 * component subscribes, so an endpoint preloaded during `getServerSideProps`
 * that answered 404 was requested again from the browser for no gain. Server
 * errors stay errors, and are still retried client-side.
 * Upstream request for a first-class option:
 * https://github.com/reduxjs/redux-toolkit/issues/1354
 * @param baseQuery - The base query to wrap.
 */
export const cacheFinalClientErrors =
  <Args, Result, QueryError, ExtraOptions, Meta>(
    baseQuery: BaseQueryFn<Args, Result, QueryError, ExtraOptions, Meta>
  ): BaseQueryFn<Args, Result | null, QueryError, ExtraOptions, Meta> =>
  async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions)

    if (isFinalClientError(result.error)) {
      return {data: null, meta: result.meta}
    }

    return result
  }
